/**
 * Liveness watchdog for a gateway socket (CB4, SPEC §7.1). A half-open socket (a phone that slept,
 * lost its network or changed cell) fires no `close` event for minutes, and until then the UI
 * would keep showing positions and P&L as live. So both stream clients ping every `intervalMs`
 * and declare the socket dead when nothing arrives within `timeoutMs` of an unanswered ping:
 *
 * - `sendPing()` is called at `start()` and every `intervalMs`; it arms the deadline unless one is
 *   already armed (the deadline counts from the **first** unanswered ping).
 * - `alive()` is called for every inbound frame: any frame proves the path works (a Pong queued
 *   behind a burst of events is still coming), so it clears the deadline.
 * - `probe()` (resume, visibility, network back): ping now with the shorter `probeTimeoutMs`, so a
 *   phone coming back shows the truth within seconds, not after the next interval.
 * - On a missed deadline the watchdog stops itself and calls `onDead` once; the client then drops
 *   the socket, shows "disconnected" and reconnects with a fresh token.
 *
 * No DOM and no runtime imports: used on the main thread and in the render worker, and tested with
 * `node:test` mock timers.
 */

/** Client ping interval (SPEC §7.1). */
export const PING_INTERVAL_MS = 5000;
/** No frame this long after a ping: the socket is treated as dead (CB4). */
export const PONG_TIMEOUT_MS = 10_000;
/** Deadline for the liveness probe on resume / visibility / network back. */
export const PROBE_TIMEOUT_MS = 4000;

export type HeartbeatOptions = {
  /** Send one ping; false when the socket cannot send (not open), which arms no deadline. */
  sendPing: () => boolean;
  /** The socket missed its deadline. Called at most once per `start()`. */
  onDead: (reason: string) => void;
  intervalMs?: number;
  timeoutMs?: number;
  probeTimeoutMs?: number;
};

export class Heartbeat {
  private interval: ReturnType<typeof setInterval> | null = null;
  private deadline: ReturnType<typeof setTimeout> | null = null;
  /** When the armed deadline expires (ms, `Date.now()` clock); lets a probe shorten it. */
  private deadlineAt = 0;
  private running = false;
  private readonly opts: HeartbeatOptions;

  constructor(opts: HeartbeatOptions) {
    this.opts = opts;
  }

  get isRunning(): boolean {
    return this.running;
  }

  /** Waiting for a frame after a ping. */
  get awaiting(): boolean {
    return this.deadline !== null;
  }

  /** Start pinging (after `Welcome`): one ping now, then every interval. Restarts when running. */
  start(): void {
    this.stop();
    this.running = true;
    this.ping(this.opts.timeoutMs ?? PONG_TIMEOUT_MS);
    this.interval = setInterval(() => {
      this.ping(this.opts.timeoutMs ?? PONG_TIMEOUT_MS);
    }, this.opts.intervalMs ?? PING_INTERVAL_MS);
  }

  stop(): void {
    this.running = false;
    if (this.interval !== null) clearInterval(this.interval);
    this.interval = null;
    this.clearDeadline();
  }

  /** Any inbound frame: the socket is alive. */
  alive(): void {
    this.clearDeadline();
  }

  /** Check now (resume): ping and expect a frame within the probe timeout. */
  probe(): void {
    if (!this.running) return;
    this.ping(this.opts.probeTimeoutMs ?? PROBE_TIMEOUT_MS);
  }

  private ping(timeoutMs: number): void {
    if (!this.running) return;
    if (!this.opts.sendPing()) return;
    const at = Date.now() + timeoutMs;
    // Keep an earlier deadline (the first unanswered ping); a probe may only bring it forward.
    if (this.deadline !== null && this.deadlineAt <= at) return;
    this.clearDeadline();
    this.deadlineAt = at;
    this.deadline = setTimeout(() => {
      this.deadline = null;
      if (!this.running) return;
      this.stop();
      this.opts.onDead(`no response from gateway for ${String(timeoutMs / 1000)} s`);
    }, timeoutMs);
  }

  private clearDeadline(): void {
    if (this.deadline !== null) clearTimeout(this.deadline);
    this.deadline = null;
  }
}
